/**
 * Market coverage utility
 * Parses and provides access to the market coverage configuration
 */

export class MarketCoverage {
  private static _makeIds: number[] | null = null;

  /**
   * Get the list of make IDs that are included in market coverage
   * Returns null if no market coverage is configured (all makes included)
   */
  static getMakeIds(): number[] | null {
    if (this._makeIds === null) {
      const coverage = process.env.REACT_APP_INTELLIJETNET_MARKET_COVERAGE;
      
      if (!coverage || coverage.trim() === '') {
        return null; // No market coverage configured - include all makes
      }

      try {
        this._makeIds = coverage
          .split(',')
          .map(id => parseInt(id.trim(), 10))
          .filter(id => !isNaN(id) && id > 0);
        
        if (this._makeIds.length === 0) {
          return null; // Invalid configuration - include all makes
        }
      } catch (error) {
        return null;
      }
    }

    return this._makeIds;
  }

  /**
   * Check if a specific make ID is included in market coverage
   */
  static includesMakeId(makeId: number): boolean {
    const makeIds = this.getMakeIds();
    return makeIds === null || makeIds.includes(makeId);
  }

  /**
   * Get a SQL IN clause for filtering by market coverage
   * Returns null if no filtering should be applied
   */
  static getSqlInClause(): string | null {
    const makeIds = this.getMakeIds();
    if (makeIds === null) return null;
    return `(${makeIds.join(',')})`;
  }

  /**
   * Clear the cached make IDs (useful for testing or when env changes)
   */
  static clearCache(): void {
    this._makeIds = null;
  }

  /**
   * Get all model IDs for the configured makes
   * This requires fetching from the database
   */
  static async getModelIds(): Promise<number[] | null> {
    const makeIds = this.getMakeIds();
    if (makeIds === null) return null;

    try {
      // Dynamic import to avoid circular dependencies
      const { AircraftModelsService } = await import('./aircraft-models.service');
      
      const models = await AircraftModelsService.query(false) // Don't apply market coverage filter again
        .in('make_id', makeIds)
        .eq('is_active', true)
        .select('model_id')
        .execute<{ model_id: number }>();
      
      return models.data.map(m => m.model_id);
    } catch (error) {
      return null;
    }
  }
}